import { Component, signal, WritableSignal, inject } from '@angular/core';
import { LocationService } from "../administration/location/location.service";
import { CustomerService } from "./customer.service";
import { LoadingState } from "../shared/loading-state.enum";
import { WeekdayService } from "../administration/weekday/weekday.service";
import { ConfirmationService, FilterMetadata, MessageService } from "primeng/api";
import { Router } from "@angular/router";
import { Status, statuses, StatusModel } from "./add-edit-customer/status-form/status.model";
import { format, subYears } from "date-fns";
import { TableLazyLoadEvent } from "primeng/table";
import { Customer } from "./customer.model";
import { AuthService } from "../login/auth.service";
import { RoleEnum } from "../login/role.enum";
import { CharacteristicService } from "../administration/characteristic/characteristic.service";

@Component({
    selector: 'app-customer-overview',
    templateUrl: './customer-overview.component.html',
    styleUrls: ['./customer-overview.component.scss'],
    providers: [ConfirmationService],
    standalone: false
})
export class CustomerOverviewComponent {
  locationService = inject(LocationService);
  customerService = inject(CustomerService);
  weekdayService = inject(WeekdayService);
  router = inject(Router);
  private confirmationService = inject(ConfirmationService);
  private messageService = inject(MessageService);
  authService = inject(AuthService);
  characteristicService = inject(CharacteristicService);

  selectedLocations: number[] | null = [];
  selectedWeekdays: number[] | null = [];
  selectedCharacteristics: number[] | null = [];
  bornAfter: Date = subYears(new Date(), 6);
  bornBefore: Date = new Date();
  ownerBornAfter: Date = new Date(1900, 0, 1);
  ownerBornBefore: Date = subYears(new Date(), 65);
  activateDate = false;
  activateOwnerDate = false;
  householdFilter = false;
  statusChange = false;
  minHousehold: number | null = null;
  maxHousehold: number | null = null;
  LoadingState = LoadingState;
  lastTableLazyLoadEvent: TableLazyLoadEvent | undefined;
  singleParent = false;
  missingFutureLocation = false;
  customers: WritableSignal<Customer[]> = signal([]);
  totalCustomers: WritableSignal<number> = signal(0)
  loading: WritableSignal<boolean> = signal(false);
  xlsxLoading: WritableSignal<boolean> = signal(false);
  memberXlsxLoading: WritableSignal<boolean> = signal(false);
  householdOptions = Array.from({ length: 20 }, (_, index) => index + 1);
  private filterTimeout: number | undefined;

  protected readonly statuses = statuses;

  get canEditCustomers(): boolean {
    const roleId = this.authService.user()?.role?.id;
    return roleId === RoleEnum.Admin || roleId === RoleEnum.Member;
  }

  async loadCustomers(event?: TableLazyLoadEvent) {
    this.loading.set(true);

    this.lastTableLazyLoadEvent = event;
    const response = await this.customerService.fetchCustomers({
      filters: this.getStrapiFiltersForEvent(event ?? {}),
      pagination: event?.first != null && event.rows != null ? {
        page: Math.floor(event.first / event.rows) + 1,
        pageSize: event.rows
      } : {},
      sort: event?.sortField && event.sortOrder ? [event.sortField + (event.sortOrder < 0 ? ":desc" : ":asc")] : [],
      fields: ['first_name', 'last_name', 'status', 'street', 'house_number', 'address_addition', 'zip', 'city'],
      single_parent: this.singleParent,
      min_household: this.householdFilter ? this.minHousehold : null,
      max_household: this.householdFilter ? this.maxHousehold : null,
      populate: {
        code: { populate: ['location'] },
        code_rotation_1: { populate: ['location'] },
        code_rotation_2: { populate: ['location'] },
        future_code: { populate: ['location'] },
        future_code_rotation_1: { populate: ['location'] },
        future_code_rotation_2: { populate: ['location'] },
        family_members: true
      },
    });
    this.customers.set(response.data);
    this.totalCustomers.set(response.meta.pagination.total);
    this.loading.set(false);
  }

  scheduleFilter(): void {
    window.clearTimeout(this.filterTimeout);
    this.filterTimeout = window.setTimeout(() => {
      void this.loadCustomers(this.lastTableLazyLoadEvent);
    }, 250);
  }

  async deleteCustomer(id: string, event: Event): Promise<void> {
    this.confirmationService.confirm({
      target: event.target ? event.target : undefined,
      message: 'Möchtest du diesen Nutzer wirklich löschen?',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: "JA",
      rejectLabel: "NEIN",
      accept: async () => {
        try {
          const customer = await this.customerService.deleteCustomer(id);
          await this.loadCustomers(this.lastTableLazyLoadEvent!);
          this.messageService.add({
            severity: 'success',
            summary: 'Nutzer gelöscht',
            detail: `${customer.first_name} ${customer.last_name} wurde gelöscht.`
          });
        } catch (e) {
          console.error(e);
          this.messageService.add({
            severity: 'error',
            summary: 'Fehler',
            detail: `Nutzer konnte nicht gelöscht werden.`
          });
        }
      }
    });

  }

  getStatusLabel(status: string): string {
    const stts = statuses.find(s => s.status == status);
    return stts?.title ?? "Unbekannt"
  }

  protected readonly RoleEnum = RoleEnum;

  async exportXLSX() {
    try {
      this.xlsxLoading.set(true);
      if (!this.lastTableLazyLoadEvent) {
        this.messageService.add({
          severity: 'error',
          summary: 'Fehler',
          detail: `Kein Event vorhanden`
        });
        return;
      }
      await this.customerService.exportXLSX({
        filters: this.getStrapiFiltersForEvent(this.lastTableLazyLoadEvent!),
        sort: this.lastTableLazyLoadEvent?.sortField && this.lastTableLazyLoadEvent.sortOrder ? [this.lastTableLazyLoadEvent.sortField + (this.lastTableLazyLoadEvent.sortOrder < 0 ? ":desc" : ":asc")] : [],
        single_parent: this.singleParent,
        min_household: this.householdFilter ? this.minHousehold : null,
        max_household: this.householdFilter ? this.maxHousehold : null,
      });
      this.messageService.add({
        severity: 'success',
        summary: 'Export',
        detail: `Daten wurden erfolgreich exportiert.`
      });
    } catch (err) {
      console.error(err);
      this.messageService.add({
        severity: 'error',
        summary: 'Fehler',
        detail: `Daten konnten nicht exportiert werden.`
      });
    } finally {
      this.xlsxLoading.set(false);
    }
  }

  async exportMemberXLSX() {
    try {
      this.memberXlsxLoading.set(true);
      if (!this.lastTableLazyLoadEvent) {
        this.messageService.add({
          severity: 'error',
          summary: 'Fehler',
          detail: `Kein Event vorhanden`
        });
        return;
      }
      await this.customerService.exportMemberXLSX({
        filters: {
          birthDate: this.activateDate && this.bornAfter ? { $between: [format(this.bornAfter, 'yyyy-MM-dd'), format(this.bornBefore, 'yyyy-MM-dd')]} : {},
          customer: this.getStrapiFiltersForEvent(this.lastTableLazyLoadEvent!)
        },
        sort: this.lastTableLazyLoadEvent?.sortField && this.lastTableLazyLoadEvent.sortOrder ? [this.lastTableLazyLoadEvent.sortField + (this.lastTableLazyLoadEvent.sortOrder < 0 ? ":desc" : ":asc")] : [],
        single_parent: this.singleParent,
        min_household: this.householdFilter ? this.minHousehold : null,
        max_household: this.householdFilter ? this.maxHousehold : null,
      });
      this.messageService.add({
        severity: 'success',
        summary: 'Export',
        detail: `Daten wurden erfolgreich exportiert.`
      });
    } catch (err) {
      console.error(err);
      this.messageService.add({
        severity: 'error',
        summary: 'Fehler',
        detail: `Daten konnten nicht exportiert werden.`
      });
    } finally {
      this.memberXlsxLoading.set(false);
    }
  }

  getStrapiFiltersForEvent(event: TableLazyLoadEvent) {
    const statuses: Status[] = (event.filters?.['status'] as FilterMetadata)?.value?.map((value: StatusModel<Status>) => value.status) ?? []
    const firstName = (event?.filters?.['first_name'] as FilterMetadata)?.value;
    const lastName = (event?.filters?.['last_name'] as FilterMetadata)?.value;
    const address = (event?.filters?.['address'] as FilterMetadata)?.value;
    const codeFilter = this.getCodeFilter((event?.filters?.['code'] as FilterMetadata)?.value);
    const selectedLocations = this.selectedLocations ?? [];
    const selectedWeekdays = this.selectedWeekdays ?? [];
    const selectedCharacteristics = this.selectedCharacteristics ?? [];
    return {
      $and: [
        {
          $or: selectedLocations.length > 0 ? [
            { code: { location: { id: { $in: selectedLocations } } } },
            { code_rotation_1: { location: { id: { $in: selectedLocations } } } },
            { code_rotation_2: { location: { id: { $in: selectedLocations } } } },
            { future_location: { id: { $in: selectedLocations } } }
          ] : []
        }, {
          $or: selectedWeekdays.length > 0 ? [
            { code: { weekday: { id: { $in: selectedWeekdays } } } },
            { code_rotation_2: { weekday: { id: { $in: selectedWeekdays } } } },
            { code_rotation_1: { weekday: { id: { $in: selectedWeekdays } } } },
          ] : []
        },
        selectedCharacteristics.length > 0 ? { characteristics: { id: { $in: selectedCharacteristics } } } : {},
        statuses.length > 0 ? { status: { $in: statuses } } : {},
        firstName ? { first_name: { $containsi: firstName } } : {},
        lastName ? { last_name: { $containsi: lastName } } : {},
        address ? {
          $or: [
            { street: { $containsi: address } },
            { house_number: { $containsi: address } },
            { zip: { $containsi: address } },
            { city: { $containsi: address } },
            { address_addition: { $containsi: address } },
          ]
        } : {},
        this.statusChange ? { $or: [{ status_change_date: { $notNull: true } }, { status_change_target: { $notNull: true } }] } : {},
        codeFilter,
        this.activateDate && this.bornAfter ? {
          family_members: { birthDate: { $between: [format(this.bornAfter, 'yyyy-MM-dd'), format(this.bornBefore, 'yyyy-MM-dd')] } },
        } : {},
        this.activateOwnerDate && this.ownerBornAfter ? {
          family_members: {
            birthDate: { $between: [format(this.ownerBornAfter, 'yyyy-MM-dd'), format(this.ownerBornBefore, 'yyyy-MM-dd')] },
            isApplicant: true,
          },
        } : {},
        this.missingFutureLocation ? { future_location: { id: { $null: true } } } : {}
      ],
    }
  }

  private getCodeFilter(input: unknown) {
    if (typeof input !== 'string' || input.trim().length === 0) {
      return {};
    }

    const parsed = this.parseCodeSearch(input);
    if (!parsed.location && !parsed.value) {
      return {};
    }

    const codeFields = parsed.rotation === 1
      ? ['code_rotation_1']
      : parsed.rotation === 2
        ? ['code_rotation_2']
        : ['code', 'code_rotation_1', 'code_rotation_2'];

    return {
      $or: codeFields.map((field) => ({
        [field]: {
          ...(parsed.location ? { location: { abbreviation: { $containsi: parsed.location } } } : {}),
          ...(parsed.value ? { value: { $containsi: parsed.value } } : {}),
        }
      }))
    };
  }

  private parseCodeSearch(input: string): { location?: string; value?: string; rotation?: 1 | 2 } {
    const normalized = input.trim().toUpperCase();
    const rotationMatch = normalized.match(/-\s*([12])\s*$/);
    const valueInput = rotationMatch ? normalized.slice(0, rotationMatch.index).trim() : normalized;
    const location = valueInput.match(/[A-ZÄÖÜ]+/g)?.join('');
    const value = valueInput.match(/\d+/g)?.join('');

    return {
      ...(location ? { location } : {}),
      ...(value ? { value } : {}),
      ...(rotationMatch ? { rotation: Number(rotationMatch[1]) as 1 | 2 } : {}),
    };
  }
}
